import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Alert,
    Chip,
    Collapse,
    Divider,
    useTheme
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
    Close as CloseIcon,
    ArrowForward as ArrowForwardIcon,
    Restore as RestoreIcon,
    Save as SaveIcon,
    Edit as EditIcon,
    SwapHoriz as SwapHorizIcon,
    Article as ArticleIcon,
} from "@mui/icons-material";
import {
    MessageSquare,
    CheckCircle2,
    Reply,
    GitCompareArrows,
    ThumbsUp,
    ThumbsDown,
    History as HistoryLucide,
    FileText,
    Download,
} from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchContractActivity, restoreFromSnapshot } from "../../../../redux/actions/contractAction";
import SnapshotViewer from "./SnapshotViewer";
import ReactGA from 'react-ga4';
import { tracker } from '../../../../analytics';
import { useTranslation } from "react-i18next";

// ─── Action config ───────────────────────────────────────────────────────────

const ACTION_LABELS = {
    // Document
    "status_change": { en: "Status change", fr: "Changement de statut", ar: "تغيير حالة العقد" },
    "update":        { en: "Content update", fr: "Modification du contenu", ar: "تحديث المحتوى" },
    "save":          { en: "Auto-save", fr: "Sauvegarde auto", ar: "حفظ" },
    "restore":       { en: "Version restored", fr: "Version restaurée", ar: "استعادة من نسخة سابقة" },
    "create":        { en: "Contract created", fr: "Contrat créé", ar: "إنشاء العقد" },
    // Comment
    "comment_added":    { en: "Comment added", fr: "Commentaire ajouté", ar: "تم إضافة تعليق" },
    "comment_resolved": { en: "Comment resolved", fr: "Commentaire résolu", ar: "تم حل التعليق" },
    "comment_reply":    { en: "Reply added", fr: "Réponse ajoutée", ar: "تم إضافة رد" },
    // Suggestion
    "suggestion_added":    { en: "Suggestion added", fr: "Suggestion ajoutée", ar: "تم إضافة اقتراح" },
    "suggestion_accepted": { en: "Suggestion accepted", fr: "Suggestion acceptée", ar: "تم قبول الاقتراح" },
    "suggestion_rejected": { en: "Suggestion rejected", fr: "Suggestion rejetée", ar: "تم رفض الاقتراح" },
};

const STATUS_LABELS = {
    "DRAFT":             { en: "Draft", fr: "Brouillon", ar: "مسودة" },
    "IN_REVIEW":         { en: "In Review", fr: "En revue", ar: "قيد المراجعة" },
    "PENDING_SIGNATURE": { en: "Pending Signature", fr: "En attente de signature", ar: "في انتظار التوقيع" },
    "SIGNED":            { en: "Signed", fr: "Signé", ar: "موقع" },
};

const STATUS_TRANSITION = {
    "changed": { en: "Status changed from", fr: "Statut modifié de", ar: "تغيرت الحالة من" },
    "to":      { en: "to", fr: "à", ar: "إلى" },
};

const FILTER_LABELS = {
    "all":        { en: "All", fr: "Tout", ar: "الكل" },
    "document":   { en: "Document", fr: "Document", ar: "مستند" },
    "comment":    { en: "Comments", fr: "Commentaires", ar: "تعليقات" },
    "suggestion": { en: "Suggestions", fr: "Suggestions", ar: "اقتراحات" },
};

// Color + icon mapping for each action type
const ACTION_CONFIG = {
    // Document actions
    save:          { color: '#4caf50', MuiIcon: SaveIcon },
    update:        { color: '#2196f3', MuiIcon: EditIcon },
    status_change: { color: '#ff9800', MuiIcon: SwapHorizIcon },
    restore:       { color: '#9c27b0', MuiIcon: RestoreIcon },
    create:        { color: '#00bcd4', MuiIcon: ArticleIcon },
    // Comment actions
    comment_added:    { color: '#6366f1', LucideIcon: MessageSquare },
    comment_resolved: { color: '#10b981', LucideIcon: CheckCircle2 },
    comment_reply:    { color: '#8b5cf6', LucideIcon: Reply },
    // Suggestion actions
    suggestion_added:    { color: '#f59e0b', LucideIcon: GitCompareArrows },
    suggestion_accepted: { color: '#10b981', LucideIcon: ThumbsUp },
    suggestion_rejected: { color: '#ef4444', LucideIcon: ThumbsDown },
};

const DEFAULT_CONFIG = { color: '#9e9e9e', MuiIcon: EditIcon };

// ─── Helpers ─────────────────────────────────────────────────────────────────

const MONTHS_FR = [
    'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
];

function formatRelativeTime(isoString) {
    try {
        const diffMs = Date.now() - new Date(isoString).getTime();
        if (diffMs < 0) return "à l'instant";
        const s = Math.floor(diffMs / 1000);
        if (s < 60) return 'à l\'instant';
        const m = Math.floor(s / 60);
        if (m < 60) return `il y a ${m}m`;
        const h = Math.floor(m / 60);
        if (h < 24) return `il y a ${h}h`;
        const d = Math.floor(h / 24);
        if (d < 30) return `il y a ${d}j`;
        const mo = Math.floor(d / 30);
        if (mo < 12) return `il y a ${mo} mois`;
        const y = Math.floor(mo / 12);
        return `il y a ${y} an${y > 1 ? 's' : ''}`;
    } catch { return ''; }
}

function formatDateGroup(isoString) {
    try {
        const date = new Date(isoString);
        const now = new Date();
        const dateOnly = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
        const dayDiff = Math.round((dateOnly(now) - dateOnly(date)) / 86400000);
        if (dayDiff === 0) return "Aujourd'hui";
        if (dayDiff === 1) return 'Hier';
        return `${date.getDate()} ${MONTHS_FR[date.getMonth()]} ${date.getFullYear()}`;
    } catch { return 'Date inconnue'; }
}

function getDayKey(isoString) {
    try {
        const d = new Date(isoString);
        return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    } catch { return 'unknown'; }
}

function groupEntriesByDay(entries) {
    const groups = [];
    const seen = new Map();
    for (const entry of entries) {
        const key = getDayKey(entry.timestamp);
        if (!seen.has(key)) {
            const group = { key, label: formatDateGroup(entry.timestamp), entries: [] };
            seen.set(key, group);
            groups.push(group);
        }
        seen.get(key).entries.push(entry);
    }
    return groups;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

const TimelineDot = ({ color }) => (
    <Box sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: color,
        flexShrink: 0,
        mt: '6px',
        boxShadow: `0 0 0 3px ${color}22`,
    }} />
);

const EntryIcon = ({ action }) => {
    const cfg = ACTION_CONFIG[action] || DEFAULT_CONFIG;
    if (cfg.LucideIcon) {
        const LIcon = cfg.LucideIcon;
        return <LIcon size={13} color={cfg.color} style={{ flexShrink: 0, marginTop: 2 }} />;
    }
    const MIcon = cfg.MuiIcon;
    return <MIcon sx={{ fontSize: 14, color: cfg.color, flexShrink: 0, mt: '2px' }} />;
};

const ContextTextPreview = ({ text }) => {
    const theme = useTheme();
    const [expanded, setExpanded] = useState(false);
    const isLong = text.length > 80;

    return (
        <Box
            onClick={isLong ? (e) => { e.stopPropagation(); setExpanded(!expanded); } : undefined}
            sx={{
                mt: 0.5,
                mb: 0.5,
                px: 0.75,
                py: 0.5,
                borderRadius: '4px',
                bgcolor: alpha(theme.palette.text.primary, 0.04),
                cursor: isLong ? 'pointer' : 'default',
                '&:hover': isLong ? { bgcolor: alpha(theme.palette.text.primary, 0.07) } : {},
                transition: 'background 0.15s',
            }}
        >
            <Typography
                variant="caption"
                sx={{
                    color: 'text.secondary',
                    fontSize: '0.68rem',
                    lineHeight: 1.4,
                    display: '-webkit-box',
                    ...(!expanded && { WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
                    wordBreak: 'break-word',
                }}
            >
                {text}
            </Typography>
            {isLong && (
                <Typography
                    variant="caption"
                    sx={{
                        color: 'primary.main',
                        fontSize: '0.62rem',
                        fontWeight: 600,
                        display: 'block',
                        mt: 0.25,
                    }}
                >
                    {expanded ? '▲ Réduire' : '▼ Voir plus'}
                </Typography>
            )}
        </Box>
    );
};

const DetailPreview = ({ entry }) => {
    const theme = useTheme();
    const detail = entry.detail;
    if (!detail) return null;

    // Comment text preview
    if (entry.type === 'comment' && detail.text) {
        return (
            <Box sx={{
                mt: 0.5,
                pl: 1,
                borderLeft: `2px solid ${alpha(theme.palette.info.main, 0.3)}`,
            }}>
                {detail.context_text && (
                    <ContextTextPreview text={detail.context_text} />
                )}
                <Typography
                    variant="caption"
                    sx={{
                        color: 'text.secondary',
                        fontStyle: 'italic',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.4,
                        fontSize: '0.7rem',
                    }}
                >
                    "{detail.text}"
                </Typography>
                {entry.action === 'comment_reply' && detail.parent_text && (
                    <Typography
                        variant="caption"
                        sx={{ color: 'text.disabled', fontSize: '0.65rem', display: 'block', mt: 0.25 }}
                    >
                        re: {detail.parent_text}
                    </Typography>
                )}
            </Box>
        );
    }

    // Suggestion diff preview
    if (entry.type === 'suggestion' && detail.original_text) {
        return (
            <Box sx={{
                mt: 0.5,
                pl: 1,
                borderLeft: `2px solid ${alpha(theme.palette.warning.main, 0.3)}`,
            }}>
                <Typography
                    variant="caption"
                    component="div"
                    sx={{ fontSize: '0.7rem', lineHeight: 1.4, color: 'text.secondary' }}
                >
                    <Box component="span" sx={{
                        textDecoration: 'line-through',
                        color: 'error.main',
                        opacity: 0.7,
                    }}>
                        {detail.original_text}
                    </Box>
                    {detail.suggested_text && (
                        <>
                            {' → '}
                            <Box component="span" sx={{ color: 'success.main', fontWeight: 500 }}>
                                {detail.suggested_text}
                            </Box>
                        </>
                    )}
                </Typography>
            </Box>
        );
    }

    return null;
};

const TimelineEntry = ({ entry, getActionLabel, onViewSnapshot }) => {
    const cfg = ACTION_CONFIG[entry.action] || DEFAULT_CONFIG;
    const relativeTime = formatRelativeTime(entry.timestamp);
    const isDocument = entry.type === 'document';

    return (
        <Box sx={{ display: 'flex', gap: 1.5, position: 'relative' }}>
            {/* Left rail */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                <TimelineDot color={cfg.color} />
                <Box sx={{ flex: 1, width: '1px', bgcolor: 'divider', minHeight: 6 }} />
            </Box>

            {/* Entry body */}
            <Box sx={{ flex: 1, pb: 1.25, minWidth: 0 }}>
                <Box
                    onClick={isDocument && entry.content_snapshot ? () => onViewSnapshot(entry) : undefined}
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 0.75,
                        cursor: isDocument && entry.content_snapshot ? 'pointer' : 'default',
                        borderRadius: 1,
                        px: 0.75,
                        py: 0.5,
                        mx: -0.75,
                        '&:hover': isDocument && entry.content_snapshot
                            ? { bgcolor: 'action.hover' }
                            : {},
                        transition: 'background 0.15s',
                    }}
                >
                    <EntryIcon action={entry.action} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, lineHeight: 1.3, fontSize: '0.78rem' }}
                        >
                            {entry.user_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
                            {getActionLabel(entry.action)}
                        </Typography>
                        <DetailPreview entry={entry} />
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.66rem' }}>
                            {relativeTime}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const History = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const dispatch = useDispatch();
    const { contractId } = useParams();
    const activity = useSelector(state => state.contract.activity || []);
    const loading = useSelector(state => state.contract.activityLoading);
    const { i18n } = useTranslation();
    const currentLanguage = i18n.language.split('-')[0] || 'en';

    const [filter, setFilter] = useState('all');
    const [selectedSnapshot, setSelectedSnapshot] = useState(null);

    const [parsedSnapshot, setParsedSnapshot] = useState(null);
    const [restoringSnapshot, setRestoringSnapshot] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

    const contract = useSelector(state => state?.contract?.contract);
    const currentUser = useSelector(state => state.auth?.user?.user);
    const contractStatus = contract?.status;
    const userRole = (contract?.collaborators || []).find(c => c.id === currentUser?.id)?.role;
    const isLocked = ['PENDING_SIGNATURE', 'SIGNED'].includes(contractStatus) || userRole === 'reviewer';

    // ── Localization helpers ──
    const getLocalizedText = useCallback((dictionary, key) => {
        if (!dictionary || !key) return key;
        if (dictionary[key]?.[currentLanguage]) return dictionary[key][currentLanguage];
        if (dictionary[key]?.en) return dictionary[key].en;
        return key;
    }, [currentLanguage]);

    const getActionLabel = useCallback((action) => getLocalizedText(ACTION_LABELS, action), [getLocalizedText]);
    const getStatusLabel = useCallback((status) => getLocalizedText(STATUS_LABELS, status), [getLocalizedText]);
    const getFilterLabel = useCallback((f) => getLocalizedText(FILTER_LABELS, f), [getLocalizedText]);

    // ── Data fetching ──
    useEffect(() => {
        if (contractId) {
            dispatch(fetchContractActivity(contractId));
        }
    }, [contractId, dispatch]);

    // ── Filtering ──
    const filteredActivity = useMemo(() => {
        if (filter === 'all') return activity;
        return activity.filter(entry => entry.type === filter);
    }, [activity, filter]);

    const groups = useMemo(() => groupEntriesByDay(filteredActivity), [filteredActivity]);

    // Count badges
    const counts = useMemo(() => ({
        all: activity.length,
        document: activity.filter(e => e.type === 'document').length,
        comment: activity.filter(e => e.type === 'comment').length,
        suggestion: activity.filter(e => e.type === 'suggestion').length,
    }), [activity]);

    // ── Snapshot dialog ──
    useEffect(() => {
        if (selectedSnapshot) {
            try {
                if (selectedSnapshot.content_snapshot &&
                    (selectedSnapshot.content_snapshot.startsWith('{') ||
                        selectedSnapshot.content_snapshot.startsWith('['))) {
                    setParsedSnapshot(JSON.parse(selectedSnapshot.content_snapshot));
                } else {
                    setParsedSnapshot(null);
                }
            } catch {
                setParsedSnapshot(null);
            }
        } else {
            setParsedSnapshot(null);
        }
    }, [selectedSnapshot]);

    const statusChangeInfo = useMemo(() => {
        if (!parsedSnapshot || !selectedSnapshot || selectedSnapshot.action !== 'status_change') return null;
        return { oldStatus: parsedSnapshot.old_status, newStatus: parsedSnapshot.new_status };
    }, [parsedSnapshot, selectedSnapshot]);

    const handleViewSnapshot = (entry) => {
        ReactGA.event({ category: 'History', action: 'View Snapshot', label: entry.action });
        tracker.event({ category: 'History', action: 'View Snapshot', label: entry.action });
        setSelectedSnapshot(entry);
    };

    const handleRestore = (entry) => {
        if (isLocked) return;
        setRestoringSnapshot(true);
        setNotification({ open: true, message: "Restoring document...", severity: "info" });

        dispatch(restoreFromSnapshot(contractId, entry.id))
            .then(() => {
                setRestoringSnapshot(false);
                setSelectedSnapshot(null);
                ReactGA.event({ category: 'History', action: 'Restore Snapshot', label: entry.action });
                tracker.event({ category: 'History', action: 'Restore Snapshot', label: entry.action });
                setNotification({ open: true, message: "Document restored successfully!", severity: "success" });
            })
            .catch((error) => {
                setRestoringSnapshot(false);
                setNotification({
                    open: true,
                    message: `Failed to restore: ${error.message || 'Unknown error'}`,
                    severity: "error"
                });
            });
    };

    const handleCloseDialog = () => {
        if (!restoringSnapshot) setSelectedSnapshot(null);
    };

    const handleDownloadHistory = () => {
        if (activity.length === 0) return;
        const text = activity.map(entry => {
            const date = new Date(entry.timestamp).toLocaleString();
            const label = getActionLabel(entry.action);
            const detail = entry.detail?.text || entry.detail?.original_text || '';
            return `${date} — ${entry.user_name} — ${label}${detail ? '\n  ' + detail : ''}`;
        }).join("\n\n");
        ReactGA.event({ category: 'History', action: 'Download History', label: `${activity.length} entries` });
        tracker.event({ category: 'History', action: 'Download History', label: `${activity.length} entries` });

        const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `contract-activity-${contractId}.txt`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const getStatusColor = (status) => {
        const map = { DRAFT: 'primary', IN_REVIEW: 'warning', PENDING_SIGNATURE: 'info', SIGNED: 'success' };
        return map[status] || 'default';
    };

    // ── Render ──

    // Only show full spinner on initial load (no data yet), not on refreshes
    if (loading && activity.length === 0) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: 'center', height: '100%', pt: 6 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            {/* ── Header ── */}
            <Box sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: 'space-between',
                pb: 1,
                flexShrink: 0,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <HistoryLucide size={16} style={{ color: theme.palette.text.secondary }} />
                    <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
                        {getLocalizedText({ activity: { en: 'Activity', fr: 'Activité', ar: 'نشاط' } }, 'activity')}
                    </Typography>
                </Box>
                <Tooltip title="Download">
                    <IconButton
                        size="small"
                        onClick={handleDownloadHistory}
                        disabled={activity.length === 0}
                        sx={{ p: 0.5 }}
                    >
                        <Download size={15} />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* ── Filter chips ── */}
            <Box sx={{
                display: 'flex',
                gap: '4px',
                pb: 1.5,
                flexShrink: 0,
                flexWrap: 'wrap',
            }}>
                {['all', 'document', 'comment', 'suggestion'].map(f => {
                    const isActive = filter === f;
                    const count = counts[f];
                    return (
                        <Box
                            key={f}
                            component="button"
                            onClick={() => setFilter(f)}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                height: 26,
                                px: 1,
                                borderRadius: '6px',
                                border: 'none',
                                fontFamily: theme.typography.fontFamily,
                                fontSize: '0.72rem',
                                fontWeight: isActive ? 600 : 400,
                                cursor: 'pointer',
                                transition: 'all 0.15s',
                                outline: 'none',
                                backgroundColor: isActive
                                    ? isDark ? alpha('#fff', 0.12) : alpha('#000', 0.07)
                                    : isDark ? alpha('#fff', 0.04) : alpha('#000', 0.02),
                                color: isActive
                                    ? theme.palette.text.primary
                                    : theme.palette.text.secondary,
                                '&:hover': {
                                    backgroundColor: isDark ? alpha('#fff', 0.1) : alpha('#000', 0.06),
                                },
                            }}
                        >
                            {getFilterLabel(f)}
                            {count > 0 && (
                                <Box component="span" sx={{
                                    fontSize: '0.62rem',
                                    fontWeight: 600,
                                    opacity: 0.6,
                                    ml: '1px',
                                }}>
                                    {count}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>

            {/* ── Timeline ── */}
            <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
                {filteredActivity.length === 0 ? (
                    <Box sx={{ pt: 6, textAlign: 'center' }}>
                        <FileText size={32} style={{ color: theme.palette.text.disabled, marginBottom: 8 }} />
                        <Typography variant="body2" color="text.secondary">
                            {filter === 'all'
                                ? (currentLanguage === 'fr' ? 'Aucune activité' : 'No activity yet')
                                : (currentLanguage === 'fr' ? 'Aucun élément' : 'No items')}
                        </Typography>
                    </Box>
                ) : (
                    groups.map((group, groupIndex) => (
                        <Box key={group.key} sx={{ mb: 0.5 }}>
                            {/* Day label */}
                            <Typography
                                variant="caption"
                                sx={{
                                    display: 'block',
                                    fontWeight: 700,
                                    color: 'text.disabled',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.06em',
                                    fontSize: '0.65rem',
                                    mb: 0.75,
                                    mt: groupIndex > 0 ? 1.5 : 0,
                                }}
                            >
                                {group.label}
                            </Typography>

                            {groupIndex > 0 && <Divider sx={{ mb: 1, opacity: 0.3 }} />}

                            {/* Entries */}
                            {group.entries.map((entry) => (
                                <TimelineEntry
                                    key={entry.id}
                                    entry={entry}
                                    getActionLabel={getActionLabel}
                                    onViewSnapshot={handleViewSnapshot}
                                />
                            ))}
                        </Box>
                    ))
                )}
            </Box>

            {/* ── Snapshot Dialog (document entries only) ── */}
            <Dialog
                open={!!selectedSnapshot}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Document Snapshot
                    <IconButton
                        onClick={handleCloseDialog}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                        disabled={restoringSnapshot}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedSnapshot && (
                        <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1">
                                    {new Date(selectedSnapshot.timestamp).toLocaleString()} — {selectedSnapshot.user_name}
                                </Typography>
                                <Chip
                                    label={getActionLabel(selectedSnapshot.action)}
                                    color={selectedSnapshot.action === 'status_change' ? 'primary' : 'default'}
                                    size="small"
                                />
                            </Box>

                            {/* Status change information */}
                            {statusChangeInfo && (
                                <Box sx={{
                                    mb: 2,
                                    p: 2,
                                    bgcolor: theme.palette.action.hover,
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    flexWrap: 'wrap'
                                }}>
                                    <Typography variant="body2" fontWeight="bold">
                                        {getLocalizedText(STATUS_TRANSITION, 'changed')}:
                                    </Typography>
                                    <Chip
                                        label={getStatusLabel(statusChangeInfo.oldStatus)}
                                        size="small"
                                        color={getStatusColor(statusChangeInfo.oldStatus)}
                                        sx={{ minWidth: 100 }}
                                    />
                                    <ArrowForwardIcon fontSize="small" />
                                    <Chip
                                        label={getStatusLabel(statusChangeInfo.newStatus)}
                                        size="small"
                                        color={getStatusColor(statusChangeInfo.newStatus)}
                                        sx={{ minWidth: 100 }}
                                    />
                                </Box>
                            )}

                            <Box sx={{ mt: 0 }}>
                                <SnapshotViewer
                                    content={statusChangeInfo ? parsedSnapshot.content : selectedSnapshot.content_snapshot}
                                />
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={restoringSnapshot}>
                        Close
                    </Button>
                    <Button
                        startIcon={restoringSnapshot ? <CircularProgress size={20} /> : <RestoreIcon />}
                        onClick={() => handleRestore(selectedSnapshot)}
                        variant="contained"
                        disabled={restoringSnapshot || isLocked}
                    >
                        {restoringSnapshot ? 'Restoring...' : getActionLabel('restore')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Snackbar ── */}
            <Snackbar
                open={notification.open}
                autoHideDuration={4000}
                onClose={() => setNotification(n => ({ ...n, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setNotification(n => ({ ...n, open: false }))}
                    severity={notification.severity}
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default History;
